import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Calendar,
  Flag,
  History,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { ru } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const priorityColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

const priorityLabels = {
  high: 'Высокий',
  medium: 'Средний',
  low: 'Низкий'
};

const statusColors = {
  pending: 'bg-gray-100 text-gray-700 border-gray-200',
  review: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200'
};

const statusLabels = {
  pending: 'Ожидает',
  review: 'На проверку',
  completed: 'Выполнено'
};

export default function ProjectTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskHistory, setSelectedTaskHistory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [expandedTasks, setExpandedTasks] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    deadline: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/api/project-tasks`, getAuthHeaders());
      setTasks(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки задач');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Введите название задачи');
      return;
    }

    try {
      if (editingTask) {
        await axios.put(`${API}/api/project-tasks/${editingTask.id}`, formData, getAuthHeaders());
        toast.success('Задача обновлена');
      } else {
        await axios.post(`${API}/api/project-tasks`, formData, getAuthHeaders());
        toast.success('Задача создана');
      }
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error('Ошибка сохранения задачи');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/api/project-tasks/${taskId}/status`, { status: newStatus }, getAuthHeaders());
      toast.success(`Статус изменён на "${statusLabels[newStatus]}"`);
      fetchTasks();
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Удалить эту задачу?')) return;
    
    try {
      await axios.delete(`${API}/api/project-tasks/${taskId}`, getAuthHeaders());
      toast.success('Задача удалена');
      fetchTasks();
    } catch (error) {
      toast.error('Ошибка удаления задачи');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', priority: 'medium', deadline: '' });
    setEditingTask(null);
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      deadline: task.deadline || ''
    });
    setDialogOpen(true);
  };

  const openHistoryDialog = (task) => {
    setSelectedTaskHistory(task);
    setHistoryDialogOpen(true);
  };

  const toggleExpanded = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const isOverdue = (deadline, status) => {
    if (!deadline || status === 'completed') return false;
    return isPast(parseISO(deadline)) && !isToday(parseISO(deadline));
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Group tasks by status
  const groupedTasks = {
    pending: filteredTasks.filter(t => t.status === 'pending'),
    review: filteredTasks.filter(t => t.status === 'review'),
    completed: filteredTasks.filter(t => t.status === 'completed')
  };

  // Stats
  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    review: tasks.filter(t => t.status === 'review').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => isOverdue(t.deadline, t.status)).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-purple-600" />
            Задачи проекта
          </h2>
          <p className="text-gray-500 mt-1">Управление задачами и контроль выполнения</p>
        </div>
        
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Новая задача
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="bg-gray-50">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
            <div className="text-xs text-gray-500">Всего</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-700">{stats.pending}</div>
            <div className="text-xs text-amber-600">Ожидают</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{stats.review}</div>
            <div className="text-xs text-blue-600">На проверку</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
            <div className="text-xs text-green-600">Выполнено</div>
          </CardContent>
        </Card>
        <Card className={`${stats.overdue > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-700' : 'text-gray-400'}`}>{stats.overdue}</div>
            <div className={`text-xs ${stats.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>Просрочено</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Поиск задач..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="pending">Ожидают</SelectItem>
            <SelectItem value="review">На проверку</SelectItem>
            <SelectItem value="completed">Выполнено</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <Flag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Приоритет" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все приоритеты</SelectItem>
            <SelectItem value="high">Высокий</SelectItem>
            <SelectItem value="medium">Средний</SelectItem>
            <SelectItem value="low">Низкий</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task Lists by Status */}
      {['pending', 'review', 'completed'].map(status => (
        <div key={status} className="space-y-3">
          <h3 className="font-semibold text-gray-700 flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${
              status === 'pending' ? 'bg-amber-500' : 
              status === 'review' ? 'bg-blue-500' : 'bg-green-500'
            }`}></span>
            {statusLabels[status]} ({groupedTasks[status].length})
          </h3>
          
          {groupedTasks[status].length === 0 ? (
            <p className="text-gray-400 text-sm italic pl-5">Нет задач</p>
          ) : (
            <div className="space-y-2">
              {groupedTasks[status].map(task => (
                <Card 
                  key={task.id} 
                  className={`transition-all ${
                    isOverdue(task.deadline, task.status) ? 'border-red-300 bg-red-50/50' : ''
                  } ${task.status === 'completed' ? 'opacity-70' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Priority indicator */}
                      <div className={`w-1 self-stretch rounded-full ${
                        task.priority === 'high' ? 'bg-red-500' :
                        task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                      }`}></div>
                      
                      <div className="flex-1 min-w-0">
                        {/* Title row */}
                        <div className="flex items-start justify-between gap-2">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => toggleExpanded(task.id)}
                          >
                            <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                              {task.title}
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${priorityColors[task.priority]}`}>
                                {priorityLabels[task.priority]}
                              </span>
                              {task.deadline && (
                                <span className={`text-xs flex items-center gap-1 ${
                                  isOverdue(task.deadline, task.status) ? 'text-red-600 font-medium' : 'text-gray-500'
                                }`}>
                                  <Calendar className="w-3 h-3" />
                                  {format(parseISO(task.deadline), 'd MMM yyyy', { locale: ru })}
                                  {isOverdue(task.deadline, task.status) && ' (просрочено)'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            {task.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusChange(task.id, 'review')}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Проверка
                              </Button>
                            )}
                            {task.status === 'review' && (
                              <Button
                                size="sm"
                                onClick={() => handleStatusChange(task.id, 'completed')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Выполнено
                              </Button>
                            )}
                            {task.status === 'completed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusChange(task.id, 'pending')}
                                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              >
                                Вернуть
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openHistoryDialog(task)}
                              className="text-gray-400 hover:text-purple-600"
                            >
                              <History className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(task)}
                              className="text-gray-400 hover:text-blue-600"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(task.id)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <button 
                              onClick={() => toggleExpanded(task.id)}
                              className="text-gray-400 hover:text-gray-600 p-1"
                            >
                              {expandedTasks[task.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        
                        {/* Expanded description */}
                        {expandedTasks[task.id] && task.description && (
                          <div className="mt-3 pt-3 border-t text-sm text-gray-600 whitespace-pre-wrap">
                            {task.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Редактировать задачу' : 'Новая задача'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Название *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Что нужно сделать?"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Подробности задачи..."
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Приоритет</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">🔴 Высокий</SelectItem>
                    <SelectItem value="medium">🟡 Средний</SelectItem>
                    <SelectItem value="low">🟢 Низкий</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="deadline">Дедлайн</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700">
              {editingTask ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              История изменений
            </DialogTitle>
          </DialogHeader>
          
          {selectedTaskHistory && (
            <div className="py-4">
              <h4 className="font-medium text-gray-800 mb-4">{selectedTaskHistory.title}</h4>
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {selectedTaskHistory.history?.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="flex gap-3 pb-3 border-b border-gray-100 last:border-0">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      entry.status === 'completed' ? 'bg-green-500' :
                      entry.status === 'review' ? 'bg-blue-500' : 'bg-amber-500'
                    }`}></div>
                    <div className="flex-1">
                      <div className="text-sm">
                        {entry.action === 'created' ? (
                          <span>Задача создана</span>
                        ) : (
                          <span>
                            Статус изменён: <span className="font-medium">{statusLabels[entry.old_status]}</span>
                            {' → '}
                            <span className="font-medium">{statusLabels[entry.status]}</span>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {entry.changed_by} • {format(parseISO(entry.changed_at), 'd MMM yyyy, HH:mm', { locale: ru })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>Закрыть</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
